import { FC, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "@/store";
import { roles } from "@/roles";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import ProductHubTable from "./ProductHubTable";

import {
  useGetTariffEngineeringCandidatesQuery,
  useRunTariffEngineeringResearchMutation,
} from "@/services/tariff-engineering";
import {
  TariffEngineeringCandidate,
  TariffEngineeringStatus,
} from "@/types/tariff-engineering";
import {
  tableCompactSx,
  tableContainerSx,
  paginationSx,
} from "@/components/table/table-styles";
import { tokens } from "@/tokens";
import { HTSCodeWithTooltip } from "@/components/hts-code-with-tooltip";

const STATUS_STYLES: Record<
  TariffEngineeringStatus,
  { label: string; color: string; bg: string }
> = {
  NOT_RUN: { label: "Not run", color: tokens.text.muted, bg: tokens.bg.hover },
  QUEUED: {
    label: "Queued",
    color: tokens.brand.primary,
    bg: `${tokens.brand.primary}14`,
  },
  RUNNING: {
    label: "Running",
    color: tokens.status.warning,
    bg: `${tokens.status.warning}14`,
  },
  DONE: {
    label: "Done",
    color: tokens.status.success,
    bg: `${tokens.status.success}14`,
  },
  FAILED: {
    label: "Failed",
    color: tokens.status.danger,
    bg: `${tokens.status.danger}14`,
  },
};

const topSavings = (c: TariffEngineeringCandidate): number | null => {
  const ops = c.latestAnalysis?.opportunities;
  if (!ops || ops.length === 0) return null;
  return Math.max(...ops.map((o) => o.savingsPct));
};

const fmtUsdCompact = (n: number): string => {
  if (!Number.isFinite(n) || n === 0) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${Math.round(n).toLocaleString()}`;
};

const fmtInt = (n: number) =>
  n === 0 ? "—" : n.toLocaleString("en-US");

const fmtPercent = (n: number) =>
  Number.isFinite(n) ? `${n.toFixed(2)}%` : "—";

const fmtRelativeDate = (s: string | null): string => {
  if (!s) return "—";
  const d = new Date(s);
  const days = Math.floor((Date.now() - d.getTime()) / 86_400_000);
  if (days < 1) return "today";
  if (days < 7) return `${days}d ago`;
  if (days < 30) return `${Math.floor(days / 7)}w ago`;
  if (days < 365) return `${Math.floor(days / 30)}mo ago`;
  return d.toLocaleDateString();
};

const StatusChip: FC<{ status: TariffEngineeringStatus }> = ({ status }) => {
  const s = STATUS_STYLES[status];
  return (
    <Chip
      label={s.label}
      size="small"
      sx={{
        height: 20,
        fontSize: tokens.fontSize["2xs"],
        fontWeight: tokens.fontWeight.semibold,
        color: s.color,
        backgroundColor: s.bg,
        border: "none",
        "& .MuiChip-label": { px: 1 },
      }}
    />
  );
};

type SubView = "trade-data" | "product-hub";

const TariffEngineering: FC = () => {
  const navigate = useNavigate();
  const { user } = useAppSelector((s) => s.auth);
  const { selectedClient } = useAppSelector((s) => s.client);
  const { selectedTenant } = useAppSelector((s) => s.tenantManagement);
  const [activeView, setActiveView] = useState<SubView>("trade-data");
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const scopeArgs = useMemo(() => {
    const args: { tenantId?: string; clientId?: string } = {};
    const isAdmin = user?.userRole === roles.TDADMIN;
    const isTDAdminTCD =
      isAdmin && selectedTenant?.tenantType === "TRADEDIVE_CLIENT";
    if (selectedTenant?._id) args.tenantId = selectedTenant._id;
    if (selectedClient?._id) {
      args.clientId = selectedClient._id;
    } else if (isTDAdminTCD && selectedTenant?._id) {
      args.clientId = selectedTenant._id;
    }
    return args;
  }, [user, selectedTenant?._id, selectedTenant?.tenantType, selectedClient?._id]);

  // Refresh-only-when-needed: poll every 5s while any row is QUEUED or
  // RUNNING so the UI catches state transitions; otherwise stop polling
  // so a tab left open doesn't hammer Mongo.
  const initialQueryArgs = useMemo(
    () => ({ ...scopeArgs, pageNo, pageSize }),
    [scopeArgs, pageNo, pageSize]
  );

  const { data, isFetching, refetch } = useGetTariffEngineeringCandidatesQuery(
    initialQueryArgs
  );

  const rows = data?.data || [];
  const isEmpty = rows.length === 0;

  const hasPendingRuns = useMemo(
    () =>
      rows.some(
        (r) => r.analysisStatus === "QUEUED" || r.analysisStatus === "RUNNING"
      ),
    [rows]
  );

  // RTK Query has built-in polling; (re)subscribe with an interval that
  // turns on when any row is mid-run and off otherwise.
  useGetTariffEngineeringCandidatesQuery(initialQueryArgs, {
    pollingInterval: hasPendingRuns ? 5000 : 0,
    skip: !hasPendingRuns,
  });

  const [runResearch] = useRunTariffEngineeringResearchMutation();
  // Track every pair currently being enqueued (the mutation returns
  // immediately so this only spans the brief enqueue window). The
  // server-side status drives the row chip after that.
  const [enqueueingKeys, setEnqueueingKeys] = useState<Set<string>>(
    () => new Set()
  );

  const handleRun = async (htsCode: string, countryOfOrigin: string) => {
    const key = `${htsCode}|${countryOfOrigin}`;
    setEnqueueingKeys((prev) => {
      const next = new Set(prev);
      next.add(key);
      return next;
    });
    try {
      await runResearch({
        ...scopeArgs,
        htsCode,
        countryOfOrigin,
      }).unwrap();
      // Mutation returns the QUEUED stub immediately; refetch picks up
      // the new candidate status so the chip flips to Queued/Running.
      refetch();
    } finally {
      setEnqueueingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  return (
    <Box sx={{ p: 3, overflow: "auto", height: "100%" }}>
      <Tabs
        value={activeView}
        onChange={(_, v: SubView) => setActiveView(v)}
        sx={{
          mb: 1.5,
          minHeight: 36,
          "& .MuiTab-root": {
            textTransform: "none",
            fontSize: tokens.fontSize.sm2,
            fontWeight: tokens.fontWeight.semibold,
            minHeight: 36,
            px: 2,
          },
          "& .MuiTabs-indicator": {
            backgroundColor: tokens.brand.primary,
            height: 2,
          },
        }}
      >
        <Tab label="Trade Data" value="trade-data" />
        <Tab label="Product Hub" value="product-hub" />
      </Tabs>

      <Typography
        sx={{
          fontSize: tokens.fontSize.sm,
          color: tokens.text.muted,
          mb: 2,
        }}
      >
        {activeView === "trade-data"
          ? "Per-(HTS, country) pairs aggregated live from your TradeData entries. Click Run on any row to queue an analysis."
          : "Your Product Hub products with today's duty rate (per-metal breakdown when applicable). Runs use the product description as additional context — Phase B adds linked chats and attached files."}
      </Typography>

      {activeView === "product-hub" ? (
        <ProductHubTable />
      ) : (
      <Paper sx={{ ...tableContainerSx, ...tableCompactSx }}>
        {isFetching && isEmpty ? (
          <Box sx={{ p: 6, textAlign: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 140 }}>HTS</TableCell>
                    <TableCell sx={{ width: 80 }}>Origin</TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>
                      # Mfrs
                    </TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>
                      Entries
                    </TableCell>
                    <TableCell align="right" sx={{ width: 110 }}>
                      Total Value
                    </TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>
                      Duty Paid
                    </TableCell>
                    <TableCell align="right" sx={{ width: 90 }}>
                      Eff. Rate
                    </TableCell>
                    <TableCell sx={{ width: 90 }}>Status</TableCell>
                    <TableCell align="right" sx={{ width: 100 }}>
                      Top Savings
                    </TableCell>
                    <TableCell sx={{ width: 100 }}>Last Run</TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>
                      &nbsp;
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((c) => {
                    const top = topSavings(c);
                    const key = `${c.htsCode}|${c.countryOfOrigin}`;
                    const isEnqueueing = enqueueingKeys.has(key);
                    const isPending =
                      c.analysisStatus === "QUEUED" ||
                      c.analysisStatus === "RUNNING";
                    return (
                      <TableRow
                        hover
                        key={key}
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(
                            `/compliance/tariff-engineering/${encodeURIComponent(c.htsCode)}/${encodeURIComponent(c.countryOfOrigin)}`
                          )
                        }
                      >
                        <TableCell>
                          <HTSCodeWithTooltip htsCode={c.htsCode}>
                            <Typography
                              sx={{
                                fontSize: tokens.fontSize.sm2,
                                fontFamily: "monospace",
                                fontWeight: tokens.fontWeight.medium,
                                color: tokens.text.primary,
                              }}
                            >
                              {c.htsCode}
                            </Typography>
                          </HTSCodeWithTooltip>
                        </TableCell>
                        <TableCell>
                          <Box
                            component="span"
                            sx={{
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              minWidth: 32,
                              height: 22,
                              px: 0.75,
                              borderRadius: tokens.radius.sm,
                              backgroundColor: tokens.bg.hover,
                              color: tokens.text.body,
                              fontSize: tokens.fontSize["2xs"],
                              fontWeight: tokens.fontWeight.semibold,
                              fontFamily: "monospace",
                              letterSpacing: "0.05em",
                            }}
                          >
                            {c.countryOfOrigin}
                          </Box>
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {fmtInt(c.distinctManufacturerCount)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {fmtInt(c.entryCount)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            fontVariantNumeric: "tabular-nums",
                            fontWeight: tokens.fontWeight.semibold,
                          }}
                        >
                          {fmtUsdCompact(c.totalEnteredValue)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {fmtUsdCompact(c.totalPaidDuty)}
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {fmtPercent(c.currentDutyRate)}
                        </TableCell>
                        <TableCell>
                          <StatusChip status={c.analysisStatus} />
                        </TableCell>
                        <TableCell
                          align="right"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          {top !== null ? (
                            <Typography
                              component="span"
                              sx={{
                                fontSize: tokens.fontSize.sm2,
                                fontWeight: tokens.fontWeight.semibold,
                                color: tokens.status.success,
                              }}
                            >
                              −{top.toFixed(1)}pp
                            </Typography>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell
                          sx={{
                            fontSize: tokens.fontSize.xs,
                            color: tokens.text.muted,
                          }}
                        >
                          {fmtRelativeDate(
                            c.latestAnalysis?.finishedAt || null
                          )}
                        </TableCell>
                        <TableCell
                          align="right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            disabled={isEnqueueing || isPending}
                            onClick={() =>
                              handleRun(c.htsCode, c.countryOfOrigin)
                            }
                            sx={{
                              minWidth: 56,
                              height: 26,
                              fontSize: tokens.fontSize.xs,
                              fontWeight: tokens.fontWeight.semibold,
                              borderRadius: tokens.radius.sm,
                              textTransform: "none",
                              py: 0,
                            }}
                          >
                            {isEnqueueing || isPending ? (
                              <CircularProgress size={12} />
                            ) : c.analysisStatus === "DONE" ? (
                              "Re-run"
                            ) : (
                              "Run"
                            )}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {isEmpty && !isFetching && (
                    <TableRow>
                      <TableCell colSpan={11} align="center">
                        <Box sx={{ py: 6 }}>
                          {!scopeArgs.tenantId || !scopeArgs.clientId ? (
                            <Typography
                              sx={{
                                fontSize: tokens.fontSize.sm,
                                color: tokens.text.muted,
                              }}
                            >
                              Select a tenant and client to load candidates.
                            </Typography>
                          ) : (
                            <Typography
                              sx={{
                                fontSize: tokens.fontSize.sm,
                                color: tokens.text.muted,
                              }}
                            >
                              No (HTS, country) pairs found in the last 12mo of
                              TradeData for this client.
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={paginationSx}>
              <TablePagination
                component="div"
                count={data?.meta.countDocs ?? 0}
                page={pageNo - 1}
                onPageChange={(_, p) => setPageNo(p + 1)}
                rowsPerPage={pageSize}
                onRowsPerPageChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPageNo(1);
                }}
              />
            </Box>
          </>
        )}
      </Paper>
      )}
    </Box>
  );
};

export default TariffEngineering;
